import { takeLatest, put, fork, all, call, select } from "redux-saga/effects"
import * as actions from "@/AppRedux/actions/booking/bookingType"
import * as actionCreation from "@/AppRedux/actions/booking"
import { SagaIterator } from "redux-saga"
import { BookingDetailType } from "@/AppRedux/actions/booking/bookingType"
import { RoomData } from "@/AppRedux/actions/booking/bookingApiType"
import { bookingDetailDefaultState } from "@/AppRedux/actions/booking/bookingType"
import { API_HEADERS, API_PMS_URL } from "@/constants/ApiSetting"
import dayjs from "dayjs"
import { APIPostRequest } from "@/services/connection"
import { RootState } from "@/AppRedux/reducers"
import { createPaymentLinkSuccess } from "@/AppRedux/actions/paymentGateway/paymentBeam"
import { getCurrentLanguage } from "@/modules/i18n/i18n.utils"

function* handleApplyCoupon(action: any): any {
  try {
    const code = action.payload.code
    const discount = code === "TEST10" ? 100 : 0
    yield put(actionCreation.applyCouponSuccess(code, discount))
  } catch (e: any) {
    yield put(actionCreation.applyCouponFailure(e.message || "Coupon failed"))
  }
}

function* handleBookingAvailability(action: any): SagaIterator {
  console.log("Fetching availability with data:", action.payload)
  const hotelProfile = yield select((state: RootState) => state.hotelProfile)
  const { propCode } = hotelProfile
  const currentLanguage = getCurrentLanguage()

  try {
    const { checkIn, checkOut, adults, children, infants } = action.payload

    const url = `${API_PMS_URL}/get_rooms_and_rates`
    const API_BODY = {
      prop_code: propCode,
      lang: currentLanguage,
      arrival: checkIn,
      departure: checkOut,
      adult: adults,
      child: children,
      infant: infants,
    }
    const response = yield call(APIPostRequest, url, API_BODY, API_HEADERS)
    if (response.status !== 200) {
      yield put(actionCreation.bookingAvailabilityFailure(response?.data?.result_msg || "Failed to fetch availability"))
      return
    }

    const { result_code, result_data } = response.data

    if (result_code !== "0" || !result_data) {
      yield put(actionCreation.setAvailability([]))
      return
    }

    //const groupedByRoomType: RoomType[] = GroupedRatesMockupSeparateBedType
    const groupedByRoomType: RoomData[] = result_data

    yield put(actionCreation.setAvailability(groupedByRoomType))
  } catch (e: any) {
    console.error("Failed to fetch availability:", e.message || "Availability fetch failed")
    yield put(actionCreation.bookingAvailabilityFailure(e.message || "Availability fetch failed"))
  }
}

function* handleCreateBookingProcess(action: any): SagaIterator {
  console.log("Creating booking with data:", action.payload)

  try {
    const { guestInfo, cart, search } = yield select((state: RootState) => state.booking)
    console.log("guestInfo ", guestInfo)
    console.log("cart ", cart)
    console.log("search ", search)
    const hotelProfile = yield select((state: RootState) => state.hotelProfile)
    const { propCode } = hotelProfile

    const { checkIn, checkOut } = search
    const redirectUrl = `${window.location.origin}/booking/${propCode}/payment/success`

    if (!checkIn || !checkOut || !guestInfo || cart.length === 0) {
      throw new Error("Missing booking information")
    }

    const details = cart.map((item: any) => {
      const rateSchedule = item.rate.rate_schedule.map((rs: any) => ({
        date: rs.date,
        amount: rs.amount,
        extra_adult_rate: rs.extra_adult_rate,
        extra_child_rate: rs.extra_child_rate,
        extra_infant_rate: rs.extra_infant_rate,
        comp: rs.comp,
        comp_abf: rs.comp_abf,
        mealtype_id: rs.mealtype_id,
        mealtype_name: rs.mealtype_name,
        total: rs.total,
      }))

      const nights = dayjs(checkOut).diff(dayjs(checkIn), "day")

      return {
        item_id: -Math.floor(Math.random() * 10000000), // Mock item_id
        roomtype_id: item.room.roomtype_id,
        roomtype_code: item.room.roomtype_code,
        roomtype_name: item.room.roomtype_name,
        rateplan_id: item.rate.rateplan_id,
        rateplan_name: item.rate.rateplan_name,
        rate_id: item.rate.rate_id,
        rate_code: item.rate.rate_code,
        arrival: dayjs(checkIn).format("YYYY/MM/DD"),
        departure: dayjs(checkOut).format("YYYY/MM/DD"),
        nights: nights,
        qty: item.qty,
        adult: search.adults,
        child: search.children,
        infant: search.infants,
        rate_schedule: rateSchedule,
        extra_adult: item.rate.extra_adult,
        extra_child: item.rate.extra_child,
        total_amount: item.rate.total_amount,
        no_amend: item.rate.no_amend,
        no_cancel: item.rate.no_cancel,
        no_refund: item.rate.no_refund,
      }
    })

    const nights = dayjs(checkOut).diff(dayjs(checkIn), "day")

    const bookingPayload = {
      prop_code: propCode,
      redirectUrl: redirectUrl,
      confirm: false,
      booking: {
        voucher_no: null,
        arrival: checkIn,
        departure: checkOut,
        details: details,
        note: guestInfo.remark || null,
        guests: [
          {
            id: -Math.floor(Math.random() * 1000000000), // Mock guest id
            first_name: guestInfo.firstName,
            last_name: guestInfo.lastName,
            contact: {
              address1: guestInfo.address1 || "",
              address2: guestInfo.address2 || "",
              emails: [guestInfo.email],
              mobiles: [guestInfo.phone],
            },
          },
        ],
        nights: nights,
      },
    }

    console.log("Constructed Booking Payload:", bookingPayload)
    
    const url = `${API_PMS_URL}/create_booking`
    const response = yield call(APIPostRequest, url, bookingPayload, API_HEADERS)

    if (response.status !== 200) {
      yield put(actionCreation.createBookingFailure("Failed to create booking"))

      return
    }
    const { result_code, result_data, result_msg } = response.data || {}
    if (result_code !== "0" || !result_data) {
      const messageFailed = result_msg ? `Failed to create booking\n(${result_msg})` : "Failed to create booking"
      yield put(actionCreation.createBookingFailure(messageFailed))
      return
    }

    /*
    // Here you would normally call an API to create the booking
    // For now, we will mock this behavior
    const bookingRef = "MOCK-BOOKING-REF-12345";
    */
    const voucherRef = result_data?.voucher_no || "Error voucher ref"
    const bookingID = result_data?.booking_id
    const paymentLinkUrl = result_data?.payment_link
    yield put(actionCreation.createBookingSuccess(voucherRef))
    yield put(createPaymentLinkSuccess(bookingID, paymentLinkUrl))
  } catch (e: any) {
    console.error("Failed to create booking:", e)
    const messageFailed = e?.message ? `Failed to create booking\n(${e.message})` : "Failed to create booking"
    yield put(actionCreation.createBookingFailure(messageFailed))
  }
}

function* handleGetBookingStatus(action: any): SagaIterator {
  const { bookingRef, email } = action.payload
  try {
    // Mock API call
    if (bookingRef === "notfound" || email === "notfound@example.com") {
      throw new Error("Booking ref. not found")
    }

    const mockBookingStatus: BookingDetailType = {
      ...bookingDetailDefaultState,
      booking_id: 123,
      booking_name: `${bookingRef}`,
      status: "CONFIRMED",
      arrival: "2025-12-24",
      departure: "2025-12-26",
      summary: {
        ...bookingDetailDefaultState.summary,
        total_charge: 5000,
        balance: 0,
        deposit: 5000,
      },
      guests: [
        {
          ...bookingDetailDefaultState.guests[0],
          first_name: "Test",
          last_name: "User",
          contact: {
            ...bookingDetailDefaultState.guests[0].contact,
            emails: [email],
          },
        },
      ],
      deposits: [
        {
          id: 1,
          status: "PAID",
          deposit_date: "2025-12-20",
          payment_name: "Credit Card",
          amount: 5000,
        },
      ],
    }
    yield put(actionCreation.getBookingStatusSuccess(mockBookingStatus))
  } catch (e: any) {
    yield put(actionCreation.getBookingStatusFailure(e.message || "Failed to get booking status"))
  }
}

/*
function* handleConfirmHold(): any {
  try {
    yield put(confirmHoldSuccess('MOCK-REF-001'));
  } catch (e: any) {
    yield put(confirmHoldFailure(e.message || 'Confirm failed'));
  }
}
*/

export function* bookingRequest() {
  yield takeLatest(actions.BOOKING_APPLY_COUPON_REQUEST, handleApplyCoupon)
}
export function* bookingAvailability() {
  yield takeLatest(actions.BOOKING_AVAILABILITY_REQUEST, handleBookingAvailability)
}
export function* bookingCreate() {
  yield takeLatest(actions.BOOKING_CREATE_REQUEST, handleCreateBookingProcess)
}
export function* bookingStatus() {
  yield takeLatest(actions.GET_BOOKING_STATUS_REQUEST, handleGetBookingStatus)
}

export default function* rootSaga() {
  yield all([fork(bookingRequest), fork(bookingAvailability), fork(bookingCreate), fork(bookingStatus)])
}
